import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const SECTION_IDS = [
    'org-settings',
    'org-hierarchy',
    'org-groups',
    'org-roles',
] as const;

export type OrgSection = typeof SECTION_IDS[number];

export const useActiveOrgSection = () => {
    const [activeSection, setActiveSection] = useState<OrgSection | null>(null);
    const location = useLocation();
    const isOnOrgPage = location.pathname === '/app/organization';
    const animationFrameRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isOnOrgPage) {
            setActiveSection(null);
            return;
        }

        const setupScrollSpy = () => {
            const container = document.getElementById('content-view');
            if (!container) return false;

            const elements = SECTION_IDS.map(id =>
                document.getElementById(id),
            ).filter(Boolean) as HTMLElement[];

            if (elements.length === 0) return false;

            const updateActiveSection = () => {
                const containerRect = container.getBoundingClientRect();
                const activationLine =
                    containerRect.top +
                    Math.min(160, containerRect.height * 0.25);
                const maxScrollTop =
                    container.scrollHeight - container.clientHeight;

                if (container.scrollTop >= maxScrollTop - 2) {
                    setActiveSection(
                        elements[elements.length - 1].id as OrgSection,
                    );
                    return;
                }

                const activeElement = elements.reduce<HTMLElement>(
                    (current, element) =>
                        element.getBoundingClientRect().top <= activationLine
                            ? element
                            : current,
                    elements[0],
                );

                setActiveSection(activeElement.id as OrgSection);
            };

            const scheduleUpdate = () => {
                if (animationFrameRef.current !== null) return;

                animationFrameRef.current = window.requestAnimationFrame(() => {
                    animationFrameRef.current = null;
                    updateActiveSection();
                });
            };

            updateActiveSection();
            container.addEventListener('scroll', scheduleUpdate, {
                passive: true,
            });
            window.addEventListener('resize', scheduleUpdate);

            const cleanup = () => {
                container.removeEventListener('scroll', scheduleUpdate);
                window.removeEventListener('resize', scheduleUpdate);

                if (animationFrameRef.current !== null) {
                    window.cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
            };

            cleanupRef.current = cleanup;
            return true;
        };

        const cleanupRef: { current?: () => void } = {};

        if (!setupScrollSpy()) {
            // Sections may not be rendered yet (data loading). Retry briefly.
            const interval = setInterval(() => {
                if (setupScrollSpy()) clearInterval(interval);
            }, 200);
            const timeout = setTimeout(() => clearInterval(interval), 3000);
            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
                cleanupRef.current?.();
            };
        }

        return () => {
            cleanupRef.current?.();
        };
    }, [isOnOrgPage]);

    const scrollToSection = useCallback((sectionId: string) => {
        const el = document.getElementById(sectionId);
        const container = document.getElementById('content-view');
        if (!el || !container) return;

        const offset =
            el.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop;

        container.scrollTo({ top: offset, behavior: 'smooth' });
    }, []);

    return { activeSection, scrollToSection };
};
